import React, { useMemo } from 'react';
import './OceanBackground.css';

const Bubble = React.memo(({ x, y, delay, size }: { 
  x: number; 
  y: number;
  delay: number;
  size: number;
}) => (
  <div 
    className="ocean-bubble"
    style={{
      '--bubble-x': x,
      '--bubble-y': y,
      '--bubble-size': size,
      animationDelay: `${delay}s`
    } as React.CSSProperties}
  />
));

const FishSilhouette = React.memo(({ x, y, delay, direction }: { 
  x: number; 
  y: number;
  delay: number;
  direction: number;
}) => (
  <div 
    className="fish-silhouette"
    style={{
      '--fish-x': x,
      '--fish-y': y,
      '--fish-direction': direction,
      animationDelay: `${delay}s`
    } as React.CSSProperties}
  />
));

const Coral = React.memo(({ x, y, type, color }: { 
  x: number; 
  y: number;
  type: string;
  color: string;
}) => (
  <div 
    className={`coral coral-${type}`}
    style={{
      left: `${x}%`,
      bottom: `${y}%`,
      '--coral-color': color
    } as React.CSSProperties}
  />
));

const OceanBackground = React.memo(() => {
  const bubbles = useMemo(() => 
    Array.from({ length: 20 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 8,
      size: 0.5 + Math.random() * 1.5
    })),
  []);

  const fishSilhouettes = useMemo(() => 
    Array.from({ length: 6 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: 30 + Math.random() * 40,
      delay: Math.random() * 12,
      direction: Math.random() > 0.5 ? 1 : -1
    })),
  []);

  const corals = useMemo(() => 
    Array.from({ length: 12 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 25,
      type: ['brain', 'fan', 'tube'][Math.floor(Math.random() * 3)],
      color: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#9B59B6'][Math.floor(Math.random() * 5)]
    })),
  []);

  return (
    <div className="ocean-background">
      {/* Water gradient */}
      <div className="ocean-water" />
      
      {/* Light rays */}
      <div className="ocean-rays">
        <div className="ray ray-1" />
        <div className="ray ray-2" />
        <div className="ray ray-3" />
        <div className="ray ray-4" />
      </div>
      
      {/* Underwater platforms/rocks */}
      <div className="ocean-platforms">
        <div className="platform platform-1" />
        <div className="platform platform-2" />
        <div className="platform platform-3" />
      </div>
      
      {/* Sand floor */}
      <div className="ocean-floor" />
      
      {/* Coral reef */}
      <div className="coral-layer">
        {corals.map(coral => (
          <Coral
            key={coral.id}
            x={coral.x}
            y={coral.y}
            type={coral.type}
            color={coral.color}
          />
        ))}
      </div>
      
      {/* Seaweed */}
      <div className="seaweed-layer">
        <div className="seaweed seaweed-1" />
        <div className="seaweed seaweed-2" />
        <div className="seaweed seaweed-3" />
        <div className="seaweed seaweed-4" />
      </div>
      
      {/* Fish silhouettes */}
      <div className="fish-layer">
        {fishSilhouettes.map(fish => (
          <FishSilhouette
            key={fish.id}
            x={fish.x}
            y={fish.y}
            delay={fish.delay}
            direction={fish.direction}
          />
        ))}
      </div>
      
      {/* Bubbles */}
      <div className="bubbles-layer">
        {bubbles.map(bubble => (
          <Bubble
            key={bubble.id}
            x={bubble.x}
            y={bubble.y}
            delay={bubble.delay}
            size={bubble.size}
          />
        ))}
      </div>
      
      {/* Water particles */}
      <div className="ocean-particles" />
    </div>
  );
});

export default OceanBackground;